// Atteindre tout le monde (D-103). Qui reçoit son accès par e-mail, et quel message part.
//  - un mineur : l'e-mail de ses parents (import FBI) ; un adulte : le sien ;
//  - un mineur sans e-mail de parent mais avec un e-mail sur sa fiche : « à vérifier » (décoché),
//    le bureau sait si c'est l'adresse de la famille ;
//  - sans e-mail : le lien de l'équipe à partager dans le groupe (un lien personnel ne se poste
//    jamais dans un groupe) ; le coach valide et la fiche est reprise.
// Pas d'écriture inclusive (D-008) : les mots suivent le genre de la personne ou de l'équipe.

export type AccessState = 'none' | 'sent' | 'linked' | 'code';
export interface AccessRow {
  person_id: string; first_name: string; last_name: string | null; nickname: string | null;
  teams: string | null; team_ids: string[]; gender: 'F' | 'M' | 'X' | null; minor: boolean;
  email: string | null; phone: string | null; parent_name: string | null; parent_email: string | null; parent_phone: string | null;
  state: AccessState; sent_at: string | null; link_created: string | null;
}
/** Une invitation à envoyer (fonction serveur `invite`) : aux parents d'un mineur, ou à l'adulte. */
export interface Planned { row: AccessRow; kind: 'parent' | 'player'; email: string; check: null | 'sheet' | 'twice' }
export interface AccessPlan { send: Planned[]; check: Planned[]; linked: AccessRow[]; noEmail: AccessRow[] }

const clean = (e: string | null) => (e ?? '').trim().toLowerCase() || null;

export function accessPlan(rows: AccessRow[]): AccessPlan {
  const plan: AccessPlan = { send: [], check: [], linked: [], noEmail: [] };
  const adults = new Map<string, string>();   // une adresse = un adulte (D-016 : pas deux fiches pour un compte)
  for (const row of rows) {
    if (row.state !== 'none') { plan.linked.push(row); continue; }
    const parent = clean(row.parent_email), own = clean(row.email);
    if (row.minor) {
      if (parent) plan.send.push({ row, kind: 'parent', email: parent, check: null });
      else if (own) plan.check.push({ row, kind: 'parent', email: own, check: 'sheet' });
      else plan.noEmail.push(row);
      continue;
    }
    if (!own) { plan.noEmail.push(row); continue; }
    if (adults.has(own)) { plan.check.push({ row, kind: 'player', email: own, check: 'twice' }); continue; }
    adults.set(own, row.person_id);
    plan.send.push({ row, kind: 'player', email: own, check: null });
  }
  return plan;
}

/** Les envois d'une même adresse se suivent (deux enfants, un parent : un seul compte créé). */
export function byAddress(list: Planned[]): Planned[][] {
  const groups = new Map<string, Planned[]>();
  for (const p of list) groups.set(p.email, [...(groups.get(p.email) ?? []), p]);
  return [...groups.values()];
}

export const nameOf = (r: Pick<AccessRow, 'first_name' | 'last_name' | 'nickname'>) =>
  [r.nickname?.trim() || r.first_name, r.last_name ? `${r.last_name.charAt(0).toUpperCase()}.` : ''].filter(Boolean).join(' ');

const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** Où en est l'accès d'une personne, en mots : « envoyé le 28 oct. », « relié »… */
export function accessLabel(r: AccessRow): { text: string; tone: 'ok' | 'warn' | 'mute' | 'ko' } {
  if (r.state === 'linked') return { text: r.minor ? 'parent relié' : 'relié', tone: 'ok' };
  if (r.state === 'code') return { text: 'code perso', tone: 'ok' };
  if (r.state === 'sent') return { text: r.sent_at ? `envoyé le ${fmtDay(r.sent_at)}` : 'envoyé', tone: 'warn' };
  if (r.minor ? r.parent_email : r.email) return { text: 'à envoyer', tone: 'mute' };
  return { text: r.link_created ? `lien créé le ${fmtDay(r.link_created)}` : 'sans e-mail', tone: 'ko' };
}

/** « 12 e-mails · 9 déjà reliés · 21 sans e-mail ». */
export function countsLine(p: Pick<AccessPlan, 'send' | 'linked' | 'noEmail'>, extra = 0): string {
  const n = p.send.length + extra, m = p.linked.length, k = p.noEmail.length;
  return `${n} ${n > 1 ? 'e-mails' : 'e-mail'} · ${m} déjà ${m > 1 ? 'reliés' : 'relié'} · ${k} sans e-mail`;
}

/** L'e-mail qui part (supabase/templates/invite.html, rôle de la fonction serveur `invite`). */
export function mailPreview(club: string, kind: 'parent' | 'player'): { subject: string; lines: string[]; button: string } {
  return {
    subject: 'Ton club t\'invite sur Arceau',
    lines: [
      `${club} t'invite sur Arceau, en tant que ${kind === 'parent' ? 'parent' : 'joueur'}.`,
      'Arceau, c\'est le planning de l\'équipe, les convocations et les présences, sur ton téléphone.',
      'Connecte-toi avec cette adresse e-mail : un code à 6 chiffres t\'arrivera. Pas de mot de passe à retenir.',
    ],
    button: 'Ouvrir Arceau',
  };
}

/** Le résultat d'un envoi (réponse de la fonction serveur `invite`), en mots. */
export function sendOutcome(r: { status?: 'invited' | 'linked'; error?: string }): { ok: boolean; text: string } {
  if (r.status === 'invited') return { ok: true, text: 'e-mail envoyé' };
  if (r.status === 'linked') return { ok: true, text: 'relié à son compte (déjà dans l’app, pas d’e-mail)' };
  const why: Record<string, string> = {
    account_in_use: 'adresse déjà prise par une autre fiche du club',
    already_linked: 'fiche déjà reliée à un autre compte',
    has_code_account: 'se connecte avec un code perso',
    input: 'adresse invalide',
    forbidden: 'pas le droit',
  };
  return { ok: false, text: why[r.error ?? ''] ?? 'envoi impossible pour le moment' };
}

// --- Les messages à partager (WhatsApp, copier) ----------------------------------------------
/** Vouvoiement quand le message va aux familles (un mineur parmi eux), sinon tutoiement. */
export const toFamilies = (people: { minor: boolean }[]) => people.some(p => p.minor);

/** Le lien de l'équipe, pour le groupe : ceux qui n'ont pas d'e-mail se présentent, le coach valide. */
export function teamLinkText(o: { club: string; team: string; url: string; families: boolean }): string {
  return o.families
    ? `Bonjour à tous ! L’app du ${o.club} arrive pour les ${o.team} : planning, convocations, covoiturage. Pas encore de compte ? Présentez-vous ici (le nom et le prénom de votre enfant), le coach valide : ${o.url}`
    : `Salut à tous ! L’app des ${o.team} (${o.club}) : planning, convocations, covoiturage. Pas encore de compte ? Présente-toi ici, le coach valide : ${o.url}`;
}

const joinLine = (families: boolean, teamUrl: string | null) => teamUrl
  ? `\nPas encore de compte ? ${families ? 'Présentez-vous' : 'Présente-toi'} ici, le coach valide : ${teamUrl}`
  : '';

/** Une annonce, pour ceux qui n'ont pas encore l'app. */
export function announcementText(o: { club: string; title: string; body: string | null; appUrl: string; teamUrl: string | null; families: boolean }): string {
  const body = (o.body ?? '').trim();
  const short = body.length > 400 ? `${body.slice(0, 397).trimEnd()}…` : body;
  return `📣 ${o.title} (${o.club})${short ? `\n${short}` : ''}\n${o.families ? 'Retrouvez' : 'Retrouve'} tout dans l’app du club : ${o.appUrl}${joinLine(o.families, o.teamUrl)}`;
}

/** Une convocation, pour ceux qui n'ont pas encore l'app. */
export function convocationText(o: {
  team: string; opponent: string | null; isHome: boolean | null; title: string; day: string; time: string;
  location: string | null; appUrl: string; teamUrl: string | null; families: boolean;
}): string {
  const what = o.opponent ? `match contre ${o.opponent}${o.isHome === true ? ' (à domicile)' : o.isHome === false ? ' (à l’extérieur)' : ''}` : o.title;
  const ask = o.families ? 'Si votre enfant ne peut pas venir, prévenez le coach.' : 'Si tu ne peux pas venir, préviens le coach.';
  return `🏀 ${o.team} · ${what} : ${o.day} à ${o.time}${o.location ? `, ${o.location}` : ''}. ${ask}\nLe détail est dans l’app du club : ${o.appUrl}${joinLine(o.families, o.teamUrl)}`;
}
